import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import { MdMic } from "@react-icons/all-files/md/MdMic";
import { MdNetworkWifi } from "@react-icons/all-files/md/MdNetworkWifi";
import { MdHeadsetMic } from "@react-icons/all-files/md/MdHeadsetMic";
import { MdVolumeOff } from "@react-icons/all-files/md/MdVolumeOff";
import { MdAccessTime } from "@react-icons/all-files/md/MdAccessTime";
import { MdVolumeUp } from "@react-icons/all-files/md/MdVolumeUp";
import { MdRefresh } from "@react-icons/all-files/md/MdRefresh";
// Importar archivos de audio con voz real
import AudioHelpModal from "../AudioHelpModal";
import audioTestFileEs from "../../assets/audio/test-message-es.mp3";
import audioTestFileEn from "../../assets/audio/test-message-en.mp3";
import {
  PreparationScreenContainer,
  PreparationContent,
  AudioSection,
  AudioCheckTitle,
  AudioCheckSubtitle,
  AudioVisualizer,
  AudioLevelIndicator,
  AudioStatus,
  AudioCheckButtons,
  AudioCheckButton,
  RequirementsSection,
  PreparationTitle,
  RequirementItem,
  RequirementIcon,
  RequirementText,
  StartButton,
  AudioTestScreen,
  AudioTestContent,
  AudioTestTitle,
  AudioTestSubtitle,
  AudioTestCard,
  AudioTestIcon,
  AudioTestMessage,
  AudioTestQuestion,
  AudioTestButtons,
  AudioTestButton,
  RefreshButton,
  AudioVisualizerIcon,
  AudioTestButtonsContainer,
  DeviceSelectorContainer,
  DeviceSelectorLabel,
  DeviceSelector,
  SpeakerSelectorRow,
  SpeakerSelectorWrapper,
  SpeakerSelectorLabel,
  SpeakerSelectorIcon,
  SpeakerSelector,
} from "./styled";

interface PreparationScreenProps {
  localAssistantStatus: string | null;
  assistantData: any;
  translations: any;
  assistantId: string;
  startCall: (deviceConfig?: {
    microphoneId?: string;
    speakerId?: string;
  }) => void;
  onMicrophoneChange?: (microphoneId: string) => void;
  onSpeakerChange?: (speakerId: string) => void;
}

const PreparationScreen: React.FC<PreparationScreenProps> = ({
  localAssistantStatus,
  assistantData,
  translations,
  assistantId,
  startCall,
  onMicrophoneChange,
  onSpeakerChange,
}) => {
  // Hook para obtener traducciones frescas
  const { t } = useTranslation();

  // Estados internos del componente
  const [isPlayingTestAudio, setIsPlayingTestAudio] = useState(false);
  const [audioTestPassed, setAudioTestPassed] = useState(false);
  const [showAudioHelpModal, setShowAudioHelpModal] = useState(false);
  const [showPreparationScreen, setShowPreparationScreen] = useState(true);
  const audioCheckStarted = useRef(false);
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
  const [hasAudioPermission, setHasAudioPermission] = useState(false);
  const [audioTestMessage, setAudioTestMessage] = useState("");
  const [audioLevel, setAudioLevel] = useState(0);
  const [showAudioTestScreen, setShowAudioTestScreen] = useState(false);
  const [isPlayingAudioTest, setIsPlayingAudioTest] = useState(false);
  const [isBlinking, setIsBlinking] = useState(false);
  const audioLevelRef = useRef(0);

  // Estados para dispositivos de audio
  const [audioDevices, setAudioDevices] = useState<{
    microphones: MediaDeviceInfo[];
    speakers: MediaDeviceInfo[];
  }>({ microphones: [], speakers: [] });
  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState<string>("");
  const [selectedSpeakerId, setSelectedSpeakerId] = useState<string>("");
  const hasCommunicatedInitialMic = useRef(false);
  const hasCommunicatedInitialSpeaker = useRef(false);

  // Memoizar el status del asistente para evitar re-renders innecesarios
  const assistantStatus = useMemo(() => {
    return localAssistantStatus || assistantData?.status;
  }, [localAssistantStatus, assistantData?.status]);

  // Función para normalizar nombres de dispositivos (para comparación)
  const normalizeDeviceName = (label: string): string => {
    if (!label) return "";

    // Remover IDs entre paréntesis
    let normalized = label.replace(/\s*\([^)]*\)\s*/g, "");

    // Remover prefijos comunes
    normalized = normalized.replace(/^Default\s+-\s*/i, "");
    normalized = normalized.replace(/^Communications\s+-\s*/i, "");
    normalized = normalized.replace(/^Predeterminado\s+-\s*/i, "");

    // Normalizar a minúsculas y limpiar espacios
    normalized = normalized.toLowerCase().replace(/\s+/g, " ").trim();

    return normalized;
  };

  // Función para acortar nombres de dispositivos
  const shortenDeviceName = (label: string): string => {
    if (!label) return "";

    // Remover IDs entre paréntesis (ej: "Razer Seiren Mini (1532:0531)" -> "Razer Seiren Mini")
    let shortened = label.replace(/\s*\([^)]*\)\s*/g, "");

    // Remover prefijos comunes innecesarios
    shortened = shortened.replace(/^Default\s+-\s*/i, "");
    shortened = shortened.replace(/^Communications\s+-\s*/i, "");
    shortened = shortened.replace(/^Predeterminado\s+-\s*/i, "");

    // Acortar palabras comunes
    shortened = shortened.replace(/\bMicrophone\b/gi, "Mic");
    shortened = shortened.replace(/\bMicrophones\b/gi, "Mic");
    shortened = shortened.replace(/\bAudio\s+Device\b/gi, "Audio");

    // Limpiar espacios múltiples
    shortened = shortened.replace(/\s+/g, " ").trim();

    // Limitar longitud máxima
    if (shortened.length > 40) {
      shortened = shortened.substring(0, 37) + "...";
    }

    return shortened || label;
  };

  // Función para enumerar dispositivos de audio
  const enumerateAudioDevices = useCallback(async () => {
    try {
      // Solicitar permisos primero para obtener dispositivos (solo si no tenemos permisos)
      if (!hasAudioPermission) {
        await navigator.mediaDevices.getUserMedia({ audio: true });
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const allMicrophones = devices.filter(
        (device) => device.kind === "audioinput"
      );
      const allSpeakers = devices.filter(
        (device) => device.kind === "audiooutput"
      );

      // Filtrar duplicados basándose en nombres normalizados
      const seenMicrophones = new Map<string, MediaDeviceInfo>();
      const seenSpeakers = new Map<string, MediaDeviceInfo>();

      allMicrophones.forEach((device) => {
        const normalizedName = normalizeDeviceName(device.label);
        // Si no existe o si el actual no tiene prefijo "Default/Predeterminado", lo reemplazamos
        if (!seenMicrophones.has(normalizedName)) {
          seenMicrophones.set(normalizedName, device);
        } else {
          const existing = seenMicrophones.get(normalizedName)!;
          const currentIsPrefixed =
            /^(Default|Predeterminado|Communications)\s+-/i.test(device.label);
          const existingIsPrefixed =
            /^(Default|Predeterminado|Communications)\s+-/i.test(
              existing.label
            );
          // Preferir el que no tenga prefijo
          if (currentIsPrefixed && !existingIsPrefixed) {
            // No hacer nada, mantener el existente
          } else if (!currentIsPrefixed && existingIsPrefixed) {
            // Reemplazar con el que no tiene prefijo
            seenMicrophones.set(normalizedName, device);
          }
        }
      });

      allSpeakers.forEach((device) => {
        const normalizedName = normalizeDeviceName(device.label);
        if (!seenSpeakers.has(normalizedName)) {
          seenSpeakers.set(normalizedName, device);
        } else {
          const existing = seenSpeakers.get(normalizedName)!;
          const currentIsPrefixed =
            /^(Default|Predeterminado|Communications)\s+-/i.test(device.label);
          const existingIsPrefixed =
            /^(Default|Predeterminado|Communications)\s+-/i.test(
              existing.label
            );
          if (currentIsPrefixed && !existingIsPrefixed) {
            // No hacer nada
          } else if (!currentIsPrefixed && existingIsPrefixed) {
            seenSpeakers.set(normalizedName, device);
          }
        }
      });

      const microphones = Array.from(seenMicrophones.values());
      const speakers = Array.from(seenSpeakers.values());

      setAudioDevices({ microphones, speakers });

      // Establecer dispositivo por defecto si no hay uno seleccionado
      setSelectedMicrophoneId((prev) => {
        if (!prev && microphones.length > 0) {
          return microphones[0].deviceId;
        }
        return prev;
      });

      setSelectedSpeakerId((prev) => {
        if (!prev && speakers.length > 0) {
          return speakers[0].deviceId;
        }
        return prev;
      });
    } catch (error) {
      console.error("Error enumerando dispositivos:", error);
    }
  }, [hasAudioPermission]);

  const playAudioTestMessage = useCallback(() => {
    setIsPlayingAudioTest(true);
    setIsBlinking(false); // Detener parpadeo al iniciar nueva reproducción

    // Crear elemento de audio para reproducir voz real
    const audio = new Audio();

    // Seleccionar archivo de audio basado en el idioma del asistente
    const audioFile =
      assistantData?.language === "es" ? audioTestFileEs : audioTestFileEn;
    audio.src = audioFile;

    // Configuración del audio
    audio.volume = 0.8;
    audio.preload = "auto";

    // Función para configurar el dispositivo de salida
    const configureAudioOutput = async () => {
      if (selectedSpeakerId && "setSinkId" in audio) {
        try {
          await (audio as any).setSinkId(selectedSpeakerId);
        } catch (err: any) {
          // Ignorar errores de configuración de dispositivo de salida
        }
      }
    };

    // Eventos del audio
    audio.oncanplaythrough = async () => {
      // Configurar el dispositivo de salida antes de reproducir
      await configureAudioOutput();

      // Esperar un momento para asegurar que setSinkId se complete
      setTimeout(() => {
        audio.play().catch((error) => {
          console.error("Error reproduciendo audio:", error);
          // Fallback a síntesis de voz si falla el audio
          playFallbackVoice();
        });
      }, 100);
    };

    audio.onended = () => {
      setIsPlayingAudioTest(false);
      // Activar parpadeo cuando termine la reproducción
      setIsBlinking(true);
      // Detener el parpadeo después de 2.2 segundos (tiempo de animación + buffer)
      setTimeout(() => {
        setIsBlinking(false);
      }, 2200);
    };

    audio.onerror = () => {
      console.error("Error cargando archivo de audio");
      // Fallback a síntesis de voz si falla el audio
      playFallbackVoice();
    };

    // Función de fallback con síntesis de voz mejorada
    const playFallbackVoice = () => {
      if ("speechSynthesis" in window) {
        const message = translations.audioTest.message;

        const utterance = new SpeechSynthesisUtterance(message);

        const isSpanish = assistantData?.language === "es";
        utterance.lang = isSpanish ? "es-ES" : "en-US";
        utterance.rate = 0.8; // Más lento
        utterance.pitch = 0.9; // Más grave
        utterance.volume = 0.8;

        utterance.onend = () => {
          setIsPlayingAudioTest(false);
          // Activar parpadeo cuando termine la reproducción
          setIsBlinking(true);
          // Detener el parpadeo después de 2.2 segundos (tiempo de animación + buffer)
          setTimeout(() => {
            setIsBlinking(false);
          }, 2200);
        };

        utterance.onerror = () => {
          setIsPlayingAudioTest(false);
        };

        speechSynthesis.speak(utterance);
      } else {
        setTimeout(() => {
          setIsPlayingAudioTest(false);
        }, 3000);
      }
    };

    // Intentar cargar el audio
    audio.load();
  }, [selectedSpeakerId, assistantData?.language]);

  const handleStartActualCall = () => {
    setShowAudioTestScreen(false);
    startCall({
      microphoneId: selectedMicrophoneId,
      speakerId: selectedSpeakerId,
    });
  };

  // Función para probar altavoces
  const testOutputAudio = () => {
    setIsPlayingTestAudio(true);
    // Reproducir el mensaje de audio real
    playAudioTestMessage();

    // Resetear el estado después de que termine la reproducción
    setTimeout(() => {
      setIsPlayingTestAudio(false);
    }, 5000); // Tiempo suficiente para que termine la reproducción
  };

  // Función para manejar respuesta de prueba de audio
  const handleAudioTestResponse = (response: boolean) => {
    setAudioTestPassed(response);
    if (response) {
      // Si escucha bien, iniciar la llamada directamente
      handleStartActualCall();
    } else {
      // Si no escucha, mostrar modal de ayuda
      setShowAudioHelpModal(true);
    }
  };

  const startAudioCheck = async () => {
    try {
      // Usar el micrófono seleccionado si está disponible
      const constraints: MediaStreamConstraints = {
        audio: selectedMicrophoneId
          ? { deviceId: { exact: selectedMicrophoneId } }
          : true,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setAudioStream(stream);
      setHasAudioPermission(true);
      setAudioTestMessage(t("call:microphone.speakNow"));

      // Configurar análisis de audio
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      let hasDetectedAudio = false;
      let animationId: number | null = null;
      let intervalId: NodeJS.Timeout | null = null;
      let isRunning = true;

      const updateAudioLevel = () => {
        if (!isRunning) return;

        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / bufferLength;

        // Solo actualizar el estado si el cambio es significativo (diferencia > 5)
        if (Math.abs(average - audioLevelRef.current) > 5) {
          audioLevelRef.current = average;
          setAudioLevel(average);
        }

        // Detectar si hay audio
        if (average > 10 && !hasDetectedAudio) {
          hasDetectedAudio = true;
          setAudioTestMessage(t("call:microphone.perfect"));
        } else if (average <= 10 && hasDetectedAudio) {
          hasDetectedAudio = false;
          setAudioTestMessage(t("call:microphone.speakNow"));
        }

        // Usar requestAnimationFrame si la pestaña está visible
        if (!document.hidden) {
          animationId = requestAnimationFrame(updateAudioLevel);
        }
      };

      // Función para iniciar la animación
      const startAnimation = () => {
        if (animationId) {
          cancelAnimationFrame(animationId);
        }
        if (intervalId) {
          clearInterval(intervalId);
        }

        // Usar requestAnimationFrame si la pestaña está visible
        if (!document.hidden) {
          animationId = requestAnimationFrame(updateAudioLevel);
        } else {
          // Usar setInterval como respaldo cuando la pestaña está en segundo plano
          intervalId = setInterval(updateAudioLevel, 100);
        }
      };
      // Detectar cambios de visibilidad de la pestaña
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          // Pestaña vuelve a estar activa
          startAnimation();
        }
      };

      // Agregar listener para cambios de visibilidad
      document.addEventListener("visibilitychange", handleVisibilityChange);

      // Iniciar la animación
      startAnimation();

      // Limpiar recursos cuando se detenga
      const cleanup = () => {
        isRunning = false;
        if (animationId) {
          cancelAnimationFrame(animationId);
        }
        if (intervalId) {
          clearInterval(intervalId);
        }
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
      };

      // Guardar función de limpieza para poder llamarla desde stopAudioCheck
      (stream as any).__cleanup = cleanup;
    } catch (error) {
      console.error("Error al iniciar verificación de audio:", error);
      setHasAudioPermission(false);
      setAudioTestMessage(translations.microphone.noAccessError);
    }
  };

  const stopAudioCheck = () => {
    if (audioStream) {
      // Llamar a la función de limpieza si existe
      if ((audioStream as any).__cleanup) {
        (audioStream as any).__cleanup();
      }

      audioStream.getTracks().forEach((track) => track.stop());
      setAudioStream(null);
      setAudioLevel(0);
      setAudioTestMessage("");
    }
    // Resetear el flag para permitir reiniciar
    audioCheckStarted.current = false;
  };

  const handleStartCall = () => {
    stopAudioCheck();
    setShowPreparationScreen(false);
    setShowAudioTestScreen(true);
    // Reproducir mensaje de prueba automáticamente
    setTimeout(() => {
      playAudioTestMessage();
    }, 500);
  };

  // Comunicar valores iniciales de micrófono y altavoz al componente padre al montar
  // NOTA: Los cambios del usuario se manejan en el onChange del selector (síncrono)
  useEffect(() => {
    // Solo comunicar el valor inicial una vez al montar
    if (
      selectedMicrophoneId &&
      onMicrophoneChange &&
      !hasCommunicatedInitialMic.current
    ) {
      onMicrophoneChange(selectedMicrophoneId);
      hasCommunicatedInitialMic.current = true;
    }
  }, [selectedMicrophoneId, onMicrophoneChange]);

  useEffect(() => {
    // Similar para el altavoz
    if (
      selectedSpeakerId &&
      onSpeakerChange &&
      !hasCommunicatedInitialSpeaker.current
    ) {
      onSpeakerChange(selectedSpeakerId);
      hasCommunicatedInitialSpeaker.current = true;
    }
  }, [selectedSpeakerId, onSpeakerChange]);

  // Enumerar dispositivos de audio al montar el componente y cuando se obtengan permisos
  useEffect(() => {
    enumerateAudioDevices();

    // Escuchar cambios en dispositivos
    const handleDeviceChange = () => {
      enumerateAudioDevices();
    };

    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        handleDeviceChange
      );
    };
  }, [enumerateAudioDevices]);

  // Reiniciar verificación cuando cambie el micrófono seleccionado
  useEffect(() => {
    if (
      selectedMicrophoneId &&
      audioCheckStarted.current &&
      hasAudioPermission &&
      showPreparationScreen &&
      assistantStatus === "waiting"
    ) {
      stopAudioCheck();
      audioCheckStarted.current = false;
      setTimeout(() => {
        if (showPreparationScreen && assistantStatus === "waiting") {
          audioCheckStarted.current = true;
          startAudioCheck();
        }
      }, 100);
    }
  }, [
    selectedMicrophoneId,
    hasAudioPermission,
    showPreparationScreen,
    assistantStatus,
  ]);

  // Iniciar verificación de audio cuando se muestre la pantalla de preparación
  useEffect(() => {
    if (
      showPreparationScreen &&
      assistantStatus === "waiting" &&
      !audioCheckStarted.current // Solo iniciar una vez
    ) {
      audioCheckStarted.current = true;
      startAudioCheck();
    }
  }, [showPreparationScreen, assistantStatus]);

  // Limpiar callId cuando el componente se desmonte
  useEffect(() => {
    return () => {
      if (assistantId) {
        sessionStorage.removeItem(`callId_${assistantId}`);
      }
      stopAudioCheck();
    };
  }, [assistantId]);

  // Limpiar estados cuando cambien las traducciones (cambio de idioma)
  useEffect(() => {
    // Resetear mensajes de audio cuando cambien las traducciones
    if (hasAudioPermission) {
      setAudioTestMessage(t("call:microphone.speakNow"));
    }
  }, [t("call:microphone.speakNow"), hasAudioPermission]);

  return (
    <>
      {/* Pantalla completa de preparación estilo Google Meet */}
      {showPreparationScreen &&
        (localAssistantStatus || assistantData?.status) === "waiting" && (
          <PreparationScreenContainer>
            <PreparationContent>
              <AudioSection>
                <AudioCheckTitle>
                  {translations.audioTest.title}
                </AudioCheckTitle>
                <AudioCheckSubtitle>
                  {translations.audioTest.subtitle}
                </AudioCheckSubtitle>

                {/* Selector de micrófono */}
                {audioDevices.microphones.length > 1 && (
                  <DeviceSelectorContainer>
                    <DeviceSelectorLabel>
                      {translations.deviceSelectors.microphone}
                    </DeviceSelectorLabel>
                    <DeviceSelector
                      value={selectedMicrophoneId}
                      onChange={(e) => {
                        const newMicrophoneId = e.target.value;
                        // CRÍTICO: Comunicar el cambio AL PADRE PRIMERO (síncrono)
                        // Esto actualiza el ref en el padre ANTES de actualizar el estado local
                        if (onMicrophoneChange) {
                          onMicrophoneChange(newMicrophoneId);
                        }
                        // Luego actualizar el estado local (asíncrono)
                        setSelectedMicrophoneId(newMicrophoneId);
                      }}
                    >
                      {audioDevices.microphones.map((device) => (
                        <option key={device.deviceId} value={device.deviceId}>
                          {device.label
                            ? shortenDeviceName(device.label)
                            : `${translations.deviceSelectors.microphone} ${
                                audioDevices.microphones.indexOf(device) + 1
                              }`}
                        </option>
                      ))}
                    </DeviceSelector>
                  </DeviceSelectorContainer>
                )}

                <AudioVisualizer>
                  <AudioLevelIndicator level={audioLevel} />
                  <AudioVisualizerIcon>
                    <MdMic size={36} />
                  </AudioVisualizerIcon>
                </AudioVisualizer>

                <AudioStatus hasPermission={hasAudioPermission}>
                  {audioTestMessage ||
                    (hasAudioPermission
                      ? translations.microphone.detected
                      : translations.microphone.noAccess)}
                </AudioStatus>

                {/* Selector de altavoces y botón en una fila */}
                {audioDevices.speakers.length > 1 ? (
                  <SpeakerSelectorRow>
                    <SpeakerSelectorWrapper>
                      <SpeakerSelectorLabel>
                        <SpeakerSelectorIcon>
                          <MdVolumeUp size={18} />
                        </SpeakerSelectorIcon>
                        {translations.deviceSelectors.speaker}
                      </SpeakerSelectorLabel>
                      <SpeakerSelector
                        value={selectedSpeakerId}
                        onChange={(e) => {
                          const newSpeakerId = e.target.value;
                          // CRÍTICO: Comunicar el cambio AL PADRE PRIMERO (síncrono)
                          if (onSpeakerChange) {
                            onSpeakerChange(newSpeakerId);
                          }
                          // Luego actualizar el estado local (asíncrono)
                          setSelectedSpeakerId(newSpeakerId);
                        }}
                      >
                        {audioDevices.speakers.map((device) => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label
                              ? shortenDeviceName(device.label)
                              : `${translations.deviceSelectors.speaker} ${
                                  audioDevices.speakers.indexOf(device) + 1
                                }`}
                          </option>
                        ))}
                      </SpeakerSelector>
                    </SpeakerSelectorWrapper>
                    <AudioCheckButton
                      variant="secondary"
                      onClick={testOutputAudio}
                      disabled={isPlayingTestAudio}
                      style={{ marginBottom: 0 }}
                    >
                      {isPlayingTestAudio
                        ? "..."
                        : translations.buttons.testSpeakers}
                    </AudioCheckButton>
                  </SpeakerSelectorRow>
                ) : (
                  <AudioCheckButtons>
                    <AudioCheckButton
                      variant="secondary"
                      onClick={testOutputAudio}
                      disabled={isPlayingTestAudio}
                    >
                      {isPlayingTestAudio
                        ? translations.playing
                        : translations.buttons.testSpeakers}
                    </AudioCheckButton>
                  </AudioCheckButtons>
                )}
              </AudioSection>
              <RequirementsSection>
                <PreparationTitle>
                  {translations.preparation.title}
                </PreparationTitle>

                <RequirementItem>
                  <RequirementIcon>
                    <MdNetworkWifi size={24} />
                  </RequirementIcon>
                  <RequirementText>
                    <strong>
                      {translations.preparation.requirements.connection.title}:
                    </strong>{" "}
                    {
                      translations.preparation.requirements.connection
                        .description
                    }
                  </RequirementText>
                </RequirementItem>

                <RequirementItem>
                  <RequirementIcon>
                    <MdHeadsetMic size={24} />
                  </RequirementIcon>
                  <RequirementText>
                    <strong>
                      {translations.preparation.requirements.headphones.title}:
                    </strong>{" "}
                    {
                      translations.preparation.requirements.headphones
                        .description
                    }
                  </RequirementText>
                </RequirementItem>

                <RequirementItem>
                  <RequirementIcon>
                    <MdVolumeOff size={24} />
                  </RequirementIcon>
                  <RequirementText>
                    <strong>
                      {translations.preparation.requirements.quietPlace.title}:
                    </strong>{" "}
                    {
                      translations.preparation.requirements.quietPlace
                        .description
                    }
                  </RequirementText>
                </RequirementItem>

                <RequirementItem>
                  <RequirementIcon>
                    <MdAccessTime size={24} />
                  </RequirementIcon>
                  <RequirementText>
                    <strong>
                      {
                        translations.preparation.requirements.timeAvailable
                          .title
                      }
                      :
                    </strong>{" "}
                    {
                      translations.preparation.requirements.timeAvailable
                        .description
                    }
                  </RequirementText>
                </RequirementItem>

                <StartButton
                  onClick={handleStartCall}
                  disabled={!hasAudioPermission}
                >
                  {hasAudioPermission
                    ? translations.buttons.startInterview
                    : translations.microphone.verifying}
                </StartButton>
              </RequirementsSection>
            </PreparationContent>
          </PreparationScreenContainer>
        )}

      {/* Pantalla de prueba de audio */}
      {showAudioTestScreen && (
        <AudioTestScreen>
          <AudioTestContent>
            <AudioTestTitle>{translations.audioTest.title}</AudioTestTitle>
            <AudioTestSubtitle>
              {translations.audioTest.subtitle}
            </AudioTestSubtitle>

            <AudioTestCard>
              <AudioTestIcon>
                <MdVolumeUp size={64} />
              </AudioTestIcon>
              <AudioTestMessage>
                {translations.audioTest.message}
              </AudioTestMessage>
              <AudioTestQuestion>
                {translations.audioTest.question}
              </AudioTestQuestion>

              {!audioTestPassed ? (
                <AudioTestButtonsContainer>
                  {/* Botón de volver a escuchar - diseño diferente */}
                  <RefreshButton
                    onClick={playAudioTestMessage}
                    disabled={isPlayingAudioTest}
                    $isBlinking={isBlinking}
                    $isPlaying={isPlayingAudioTest}
                  >
                    <MdRefresh size={20} />
                    {isPlayingAudioTest
                      ? translations.playing
                      : translations.audioTest.listenAgain}
                  </RefreshButton>

                  {/* Botones de respuesta */}
                  <AudioTestButtons>
                    <AudioTestButton
                      variant="success"
                      onClick={() => handleAudioTestResponse(true)}
                      disabled={isPlayingAudioTest}
                    >
                      {translations.audioTest.yesButton}
                    </AudioTestButton>
                    <AudioTestButton
                      variant="danger"
                      onClick={() => handleAudioTestResponse(false)}
                      disabled={isPlayingAudioTest}
                    >
                      {translations.audioTest.noButton}
                    </AudioTestButton>
                  </AudioTestButtons>
                </AudioTestButtonsContainer>
              ) : (
                <AudioTestButtons>
                  <AudioTestButton
                    variant="success"
                    onClick={() => handleAudioTestResponse(true)}
                    disabled={isPlayingAudioTest}
                  >
                    {translations.audioTest.yesButton}
                  </AudioTestButton>
                  <AudioTestButton
                    variant="danger"
                    onClick={() => handleAudioTestResponse(false)}
                    disabled={isPlayingAudioTest}
                  >
                    {translations.audioTest.noButton}
                  </AudioTestButton>
                </AudioTestButtons>
              )}
            </AudioTestCard>
          </AudioTestContent>
        </AudioTestScreen>
      )}

      {/* Modal de ayuda de audio */}
      <AudioHelpModal
        isOpen={showAudioHelpModal}
        onClose={() => setShowAudioHelpModal(false)}
        onGoBack={() => setShowAudioHelpModal(false)}
        onTryAgain={() => {
          setShowAudioHelpModal(false);
          playAudioTestMessage();
        }}
        translations={translations}
      />
    </>
  );
};

export default PreparationScreen;
